/* eslint-disable no-unused-vars */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  addDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebaseFunctions/config';

export const vendorCRMService = {
  async getVendorDirectory(eoUserId) {
    try {
      const vendorsRef = collection(db, 'vendorCRM', eoUserId, 'vendors');
      const q = query(vendorsRef, orderBy('lastInteraction', 'desc'));
      const snapshot = await getDocs(q);
      
      const vendors = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return { success: true, vendors };
    } catch (error) {
      console.error('Error fetching vendor directory:', error);
      return { success: false, message: error.message, vendors: [] };
    }
  },

  async addVendorToDirectory(eoUserId, vendorData) {
    try {
      const vendorsRef = collection(db, 'vendorCRM', eoUserId, 'vendors');
      
      const existingQuery = query(vendorsRef, where('vendorUserId', '==', vendorData.vendorUserId));
      const existingSnapshot = await getDocs(existingQuery);
      
      if (!existingSnapshot.empty) {
        const existingDoc = existingSnapshot.docs[0];
        await updateDoc(doc(vendorsRef, existingDoc.id), {
          ...vendorData,
          lastInteraction: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        return { success: true, vendorId: existingDoc.id, updated: true };
      }

      const newVendor = {
        ...vendorData,
        status: 'active',
        isFavorite: false,
        isBlocked: false,
        tags: [],
        eventsAttended: vendorData.eventsAttended || 0,
        totalRevenue: vendorData.totalRevenue || 0,
        lastInteraction: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(vendorsRef, newVendor);
      return { success: true, vendorId: docRef.id, updated: false };
    } catch (error) {
      console.error('Error adding vendor to directory:', error);
      return { success: false, message: error.message };
    }
  },

  async getVendorDetails(eoUserId, vendorId) {
    try {
      const vendorRef = doc(db, 'vendorCRM', eoUserId, 'vendors', vendorId);
      const vendorSnap = await getDoc(vendorRef);

      if (!vendorSnap.exists()) {
        return { success: false, message: 'Vendor not found' };
      }

      const notesRef = collection(db, 'vendorCRM', eoUserId, 'vendors', vendorId, 'notes');
      const notesQuery = query(notesRef, orderBy('createdAt', 'desc'));
      const notesSnapshot = await getDocs(notesQuery);
      const notes = notesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const historyRef = collection(db, 'vendorCRM', eoUserId, 'vendors', vendorId, 'eventHistory');
      const historyQuery = query(historyRef, orderBy('eventDate', 'desc'));
      const historySnapshot = await getDocs(historyQuery);
      const eventHistory = historySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        success: true,
        vendor: { id: vendorSnap.id, ...vendorSnap.data() },
        notes,
        eventHistory
      };
    } catch (error) {
      console.error('Error fetching vendor details:', error);
      return { success: false, message: error.message };
    }
  },

  async updateVendor(eoUserId, vendorId, updates) {
    try {
      const vendorRef = doc(db, 'vendorCRM', eoUserId, 'vendors', vendorId);
      await updateDoc(vendorRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating vendor:', error);
      return { success: false, message: error.message };
    }
  },

  async toggleFavorite(eoUserId, vendorId, isFavorite) {
    try {
      const vendorRef = doc(db, 'vendorCRM', eoUserId, 'vendors', vendorId);
      await updateDoc(vendorRef, {
        isFavorite,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return { success: false, message: error.message };
    }
  },

  async toggleBlocked(eoUserId, vendorId, isBlocked) {
    try {
      const vendorRef = doc(db, 'vendorCRM', eoUserId, 'vendors', vendorId);
      await updateDoc(vendorRef, {
        isBlocked,
        status: isBlocked ? 'blocked' : 'active',
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error toggling blocked status:', error);
      return { success: false, message: error.message };
    }
  },

  async addTag(eoUserId, vendorId, tag) {
    try {
      const vendorRef = doc(db, 'vendorCRM', eoUserId, 'vendors', vendorId);
      const vendorSnap = await getDoc(vendorRef);
      
      if (!vendorSnap.exists()) {
        return { success: false, message: 'Vendor not found' };
      }

      const currentTags = vendorSnap.data().tags || [];
      if (!currentTags.includes(tag)) {
        await updateDoc(vendorRef, {
          tags: [...currentTags, tag],
          updatedAt: serverTimestamp()
        });
      }
      return { success: true };
    } catch (error) {
      console.error('Error adding tag:', error);
      return { success: false, message: error.message };
    }
  },

  async removeTag(eoUserId, vendorId, tag) {
    try {
      const vendorRef = doc(db, 'vendorCRM', eoUserId, 'vendors', vendorId);
      const vendorSnap = await getDoc(vendorRef);
      
      if (!vendorSnap.exists()) {
        return { success: false, message: 'Vendor not found' };
      }

      const currentTags = vendorSnap.data().tags || [];
      await updateDoc(vendorRef, {
        tags: currentTags.filter(t => t !== tag),
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error removing tag:', error);
      return { success: false, message: error.message };
    }
  },

  async addNote(eoUserId, vendorId, noteContent) {
    try {
      const notesRef = collection(db, 'vendorCRM', eoUserId, 'vendors', vendorId, 'notes');
      const noteDoc = await addDoc(notesRef, {
        content: noteContent,
        createdAt: serverTimestamp()
      });

      const vendorRef = doc(db, 'vendorCRM', eoUserId, 'vendors', vendorId);
      await updateDoc(vendorRef, {
        lastInteraction: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true, noteId: noteDoc.id };
    } catch (error) {
      console.error('Error adding note:', error);
      return { success: false, message: error.message };
    }
  },

  async deleteNote(eoUserId, vendorId, noteId) {
    try {
      const noteRef = doc(db, 'vendorCRM', eoUserId, 'vendors', vendorId, 'notes', noteId);
      await deleteDoc(noteRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting note:', error);
      return { success: false, message: error.message };
    }
  },

  async recordEventParticipation(eoUserId, vendorId, eventData) {
    try {
      const historyRef = collection(db, 'vendorCRM', eoUserId, 'vendors', vendorId, 'eventHistory');
      await addDoc(historyRef, {
        eventId: eventData.eventId,
        eventName: eventData.eventName,
        eventDate: eventData.eventDate,
        revenue: eventData.revenue || 0,
        status: eventData.status,
        rating: eventData.rating || null,
        createdAt: serverTimestamp()
      });

      const vendorRef = doc(db, 'vendorCRM', eoUserId, 'vendors', vendorId);
      const vendorSnap = await getDoc(vendorRef);
      
      if (vendorSnap.exists()) {
        const currentData = vendorSnap.data();
        await updateDoc(vendorRef, {
          eventsAttended: (currentData.eventsAttended || 0) + 1,
          totalRevenue: (currentData.totalRevenue || 0) + (eventData.revenue || 0),
          lastInteraction: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Error recording event participation:', error);
      return { success: false, message: error.message };
    }
  },

  async syncVendorsFromApplications(eoUserId) {
    try {
      const applicationsRef = collection(db, 'mpsEventRegistration');
      const eventsRef = collection(db, 'events');

      const eventsQuery = query(eventsRef, where('userId', '==', eoUserId));
      const eventsSnapshot = await getDocs(eventsQuery);

      if (eventsSnapshot.empty) {
        return { success: true, synced: 0 };
      }

      // Build a map of eventId -> event data
      const eventMap = {};
      eventsSnapshot.docs.forEach(d => {
        eventMap[d.id] = { id: d.id, ...d.data() };
      });

      let syncedCount = 0;

      for (const eventId of Object.keys(eventMap)) {
        const event = eventMap[eventId];
        const appQuery = query(applicationsRef, where('eventId', '==', eventId));
        const appSnapshot = await getDocs(appQuery);

        for (const appDoc of appSnapshot.docs) {
          const app = appDoc.data();
          if (!app.userId) continue;

          const vendorData = {
            vendorUserId: app.userId,
            businessName: app.businessName,
            businessEmail: app.businessEmail,
            businessPhone: app.businessPhoneNumber,
            businessType: app.businessType,
            businessDescription: app.businessDescription,
            companyLogoURL: app.companyLogoURL,
            fullName: app.fullName
          };

          const result = await this.addVendorToDirectory(eoUserId, vendorData);
          if (!result.success) continue;
          if (!result.updated) syncedCount++;

          // Write event history using eventId as doc ID — prevents duplicates on re-sync
          const historyDocRef = doc(
            db, 'vendorCRM', eoUserId, 'vendors', result.vendorId, 'eventHistory', eventId
          );
          await setDoc(historyDocRef, {
            eventId,
            eventName: event.eventName || event.name || 'Unnamed Event',
            eventDate: event.startDate || event.eventDate || null,
            revenue: app.feePaid || app.revenue || 0,
            status: app.approvalStatus || app.status || 'Applied',
            updatedAt: serverTimestamp()
          }, { merge: true });
        }
      }

      // Recalculate eventsAttended + totalRevenue for all vendors from history
      const vendorsRef = collection(db, 'vendorCRM', eoUserId, 'vendors');
      const vendorsSnapshot = await getDocs(vendorsRef);

      await Promise.all(vendorsSnapshot.docs.map(async (vendorDoc) => {
        const historyRef = collection(db, 'vendorCRM', eoUserId, 'vendors', vendorDoc.id, 'eventHistory');
        const historySnapshot = await getDocs(historyRef);
        const totalRevenue = historySnapshot.docs.reduce((sum, h) => sum + (h.data().revenue || 0), 0);
        await updateDoc(doc(vendorsRef, vendorDoc.id), {
          eventsAttended: historySnapshot.size,
          totalRevenue,
          updatedAt: serverTimestamp()
        });
      }));

      return { success: true, synced: syncedCount };
    } catch (error) {
      console.error('Error syncing vendors from applications:', error);
      return { success: false, message: error.message };
    }
  },

  async searchVendors(eoUserId, searchTerm, filters = {}) {
    try {
      const vendorsRef = collection(db, 'vendorCRM', eoUserId, 'vendors');
      let q = query(vendorsRef);

      if (filters.isFavorite !== undefined) {
        q = query(q, where('isFavorite', '==', filters.isFavorite));
      }

      if (filters.isBlocked !== undefined) {
        q = query(q, where('isBlocked', '==', filters.isBlocked));
      }

      if (filters.businessType) {
        q = query(q, where('businessType', '==', filters.businessType));
      }

      const snapshot = await getDocs(q);
      let vendors = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        vendors = vendors.filter(v => 
          v.businessName?.toLowerCase().includes(term) ||
          v.businessEmail?.toLowerCase().includes(term) ||
          v.fullName?.toLowerCase().includes(term) ||
          v.tags?.some(tag => tag.toLowerCase().includes(term))
        );
      }

      if (filters.tag) {
        vendors = vendors.filter(v => v.tags?.includes(filters.tag));
      }

      return { success: true, vendors };
    } catch (error) {
      console.error('Error searching vendors:', error);
      return { success: false, message: error.message, vendors: [] };
    }
  },

  async getAllTags(eoUserId) {
    try {
      const vendorsRef = collection(db, 'vendorCRM', eoUserId, 'vendors');
      const snapshot = await getDocs(vendorsRef);
      
      const allTags = new Set();
      snapshot.docs.forEach(doc => {
        const tags = doc.data().tags || [];
        tags.forEach(tag => allTags.add(tag));
      });

      return { success: true, tags: Array.from(allTags).sort() };
    } catch (error) {
      console.error('Error fetching tags:', error);
      return { success: false, message: error.message, tags: [] };
    }
  },

  async getVendorStats(eoUserId) {
    try {
      const vendorsRef = collection(db, 'vendorCRM', eoUserId, 'vendors');
      const snapshot = await getDocs(vendorsRef);
      
      const stats = {
        total: 0,
        favorites: 0,
        blocked: 0,
        byCategory: {},
        totalRevenue: 0
      };

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        stats.total++;
        if (data.isFavorite) stats.favorites++;
        if (data.isBlocked) stats.blocked++;
        if (data.businessType) {
          stats.byCategory[data.businessType] = (stats.byCategory[data.businessType] || 0) + 1;
        }
        stats.totalRevenue += data.totalRevenue || 0;
      });

      return { success: true, stats };
    } catch (error) {
      console.error('Error fetching vendor stats:', error);
      return { success: false, message: error.message };
    }
  }
};
